'use client'

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { EmblaCarouselType } from 'embla-carousel'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/shared/ui/carousel'
import Autoplay from 'embla-carousel-autoplay'
import { cn } from '@/shared/lib'

const AUTOPLAY_DELAY = 4000
const AFTER_INTERACTION_DELAY = 6000

type SliderDotsProps = {
  total: number
  activeIndex: number
  className?: string
}

export const SliderDots = ({ total, activeIndex, className }: SliderDotsProps) => {
  return (
    <div className={cn('flex justify-center items-center gap-1', className)}>
      {Array.from({ length: total }).map((_, index) => (
        <div
          key={index}
          className={cn('h-1 rounded-full transition-all duration-300 w-2 bg-primary', index === activeIndex && 'w-10')}
        />
      ))}
    </div>
  )
}

export const GenericCarousel = <T,>({ content, render }: { content: T[]; render: (item: T) => ReactNode }) => {
  const doubled = content.length > 2 && content.length < 5
  const items = doubled ? content.concat(content) : content
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaThumbsApi, setEmblaThumbsApi] = useState<EmblaCarouselType>()
  const [interacted, setInteracted] = useState(false)
  const timeoutIdRef = useRef<number>(0)

  const handleInteraction = () => {
    setInteracted(true)
    const { current: prevId } = timeoutIdRef
    clearTimeout(prevId)
    timeoutIdRef.current = setTimeout(() => {
      setInteracted(false)
    }, AFTER_INTERACTION_DELAY) as never
  }

  useEffect(() => {
    if (!emblaThumbsApi) return
    emblaThumbsApi.scrollTo(selectedIndex)
  }, [emblaThumbsApi, selectedIndex])

  const onThumbSelect = useCallback(() => {
    if (!emblaThumbsApi) return
    setSelectedIndex(emblaThumbsApi.selectedScrollSnap())
  }, [emblaThumbsApi, setSelectedIndex])

  useEffect(() => {
    if (!emblaThumbsApi) return
    emblaThumbsApi.on('select', onThumbSelect).on('reInit', onThumbSelect)
    return () => {
      emblaThumbsApi.off('select', onThumbSelect).on('reInit', onThumbSelect)
    }
  }, [emblaThumbsApi, onThumbSelect])
  return (
    <div
      className={`flex flex-col w-full gap-2}`}
      onMouseEnter={handleInteraction}
      onMouseMove={handleInteraction}
      onClick={handleInteraction}
      onFocus={handleInteraction}
      onTouchStart={handleInteraction}
      onTouchMove={handleInteraction}
    >
      <Carousel
        plugins={[
          Autoplay({
            delay: AUTOPLAY_DELAY,
            active: !interacted,
          }),
        ]}
        className="w-full flex flex-col gap-2"
        opts={{ loop: true, align: 'center' }}
        setApi={setEmblaThumbsApi}
      >
        <CarouselContent className={'w-full'}>
          {items.map((item, index) => {
            return (
              <CarouselItem
                key={index}
                className={cn(
                  `group w-full select-none flex flex-col items-center justify-center gap-2`,
                  content.length === 2 && 'sm:w-1/2 sm:basis-1/2',
                  content.length >= 3 && 'sm:w-1/3 sm:basis-1/3',
                )}
              >
                {render(item)}
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <div className={'w-full flex flex-row items-center'}>
          <SliderDots className={'grow'} activeIndex={selectedIndex % content.length} total={content.length} />
          <div className={'hidden sm:flex flex-row items-center justify-start self-end'}>
            <CarouselPrevious absolute={false} />
            <CarouselNext absolute={false} />
          </div>
        </div>
      </Carousel>
    </div>
  )
}
